import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import AnswerBoxTop from "@/components/AnswerBoxTop";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { HubCountryLinks } from "@/components/HubCountryLinks";
// /apostille — hub page for all destination countries
import { createFileRoute, Link } from "@tanstack/react-router";
import { DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { VoiceSearchFAQ } from "@/components/seo/VoiceSearchFAQ";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { APOSTILLE_COUNTRIES, APOSTILLE_REGIONS } from "@/data/apostille-countries";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { Stamp, Globe2, ShieldCheck, MessageCircle, Phone } from "lucide-react";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/apostille/")({
  loader: async () => ({
    clusterFaq: await buildClusterFaqJsonLdAsync("apostille", "th", "https://nyclanguageinstitute.com/apostille"),
    voiceFaqs: (await import("@/data/voice-faqs")).APOSTILLE_VOICE_FAQS,
  }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/apostille`;
    const title = "บริการ Apostille / Legalization เอกสารไทย ใช้ต่างประเทศ 50+ ประเทศ | NYC";
    const desc = "รับรอง Apostille (Hague) และ Consular Legalization เอกสารไทยเพื่อใช้ใน 50+ ประเทศ — รวมแปล + MFA + สถานทูต เสร็จ 5-7 วัน — แจ้งเอกสารที่มี รู้ขั้นตอนและระยะเวลาใน 10 นาที";
    return {
      meta: [
        { title: ctaTitle(title, "/apostille", "th") }, { name: "description", content: ctaDesc(desc, "/apostille", "th")},
        { property: "og:title", content: ctaTitle(title, "/apostille", "th")}, { property: "og:description", content: ctaDesc(desc, "/apostille", "th")},
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/apostille")],
      scripts: [speakableSchema(),
        serviceWithReviewsSchema({ path: "/apostille", serviceType: "Apostille and Document Legalization", name: title, description: desc, priceFromThb: 3500 }),
        howToSchema({
          name: "วิธีขอ Apostille / Consular Legalization เอกสารไทยเพื่อใช้ต่างประเทศ",
          description: "ขั้นตอนรับรองเอกสารไทย (ทะเบียนสมรส · สูติบัตร · ใบรับรองโสด · หนังสือมอบอำนาจ · วุฒิการศึกษา) สำหรับใช้ใน 50+ ประเทศ — รวมแปล + กรมการกงสุล (MFA) + สถานทูต",
          totalTime: "PT7D",
          estimatedCost: { currency: "THB", value: "3500" },
          steps: [
            { name: "ขอเอกสารต้นฉบับจากหน่วยงานราชการ", text: "ขอ ทะเบียนบ้าน · สูติบัตร · ทะเบียนสมรส · ใบรับรองโสด (คร.22) จากเขต/อำเภอ หรือ Affidavit จากสถานทูต", url: `${SITE}/notary` },
            { name: "แปลเป็นภาษาอังกฤษโดยนักแปลรับรอง", text: "แปลโดยทีม NYC ที่ขึ้นทะเบียนกระทรวงยุติธรรม + Notary รับรองคำแปล (Translator's Affidavit)", url: `${SITE}/translate` },
            { name: "รับรองที่กรมการกงสุล (MFA) ถนนแจ้งวัฒนะ", text: "ส่งเอกสาร + คำแปลเข้ารับรองที่กองสัญชาติและนิติกรณ์ กรมการกงสุล — ใช้เวลา 2-3 วันทำการ" },
            { name: "Apostille (Hague) หรือ Consular Legalization", text: "ประเทศสมาชิก Hague ใช้ Apostille ผ่าน MFA ได้เลย · ประเทศนอก Hague (จีน UAE ซาอุฯ) ต้องส่งสถานทูตประเทศปลายทางต่อ", url: `${url}#regions` },
            { name: "รับเอกสารพร้อมใช้งานในต่างประเทศ", text: "รับเอกสารต้นฉบับ + คำแปล + Apostille/Legalization ภายใน 5-7 วันทำการ ส่ง EMS ทั่วประเทศ / DHL ต่างประเทศได้" },
          ],
        }),
        {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Apostille / Legalization Destinations",
          url,
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Apostille / Legalization", item: url },
            ],
          },
          hasPart: APOSTILLE_COUNTRIES.map((c) => ({
            "@type": "Service",
            name: `Apostille for ${c.nameEn}`,
            url: `${url}/${c.slug}`,
          })),
        }),
      },
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: ApostilleHub,
});

function ApostilleHub() {
  const { voiceFaqs } = Route.useLoaderData();
  return (
    <main className="min-h-screen bg-white">
      <section className="relative bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">Apostille / Legalization</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stamp className="h-3.5 w-3.5" /> Apostille · Hague Convention 1961
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Apostille / Legalization เอกสารไทย<br className="hidden sm:block" />
            <span className="text-gold">ใช้ได้ทั่วโลก 50+ ประเทศ</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            ประเทศไทยเข้าร่วม Hague Apostille Convention เมื่อ 30 มิ.ย. 2569 (มีผลบังคับใช้กับไทย 28 ก.พ. 2570) — เราจัดการให้ครบทุกขั้นตอน: แปล → รับรองนักแปล → MFA Apostille หรือ <Link to="/mfa-legalisation" className="underline decoration-amber-300 underline-offset-4 hover:text-amber-200">MFA + Consular Legalisation สำหรับประเทศนอก Hague</Link> (จีน, UAE, ซาอุฯ, เวียดนาม, ไต้หวัน)
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity="NYC Apostille Thailand">
              NYC Language Institute ดำเนินการ Apostille และ Consular Legalization เอกสารไทยครบทุกขั้นตอน — แปลรับรอง MoJ → รับรองนิติกรณ์ที่กรมการกงสุล (MFA แจ้งวัฒนะ) → รับรองที่สถานทูตปลายทาง โดยทั่วไป 5–14 วันทำการ ขึ้นกับคิวของหน่วยงาน — สำหรับประเทศสมาชิกอนุสัญญา Hague (130 ภาคี) เอกสารไทยจะใช้ Apostille แทนขั้นตอนสถานทูตได้เมื่ออนุสัญญามีผลบังคับใช้กับไทยในวันที่ 28 กุมภาพันธ์ 2570 (ที่มา: HCCH)
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "ครอบคลุม", v: "130 ประเทศ Hague + นอก Hague" },
              { k: "ระยะเวลา", v: "5–14 วันทำการ" },
              { k: "ผู้ออกตรา", v: "กรมการกงสุล กระทรวงต่างประเทศ" },
              { k: "ประเภทเอกสาร", v: "ทะเบียน · การศึกษา · พาณิชย์" },
              { k: "Express", v: "2–3 วันทำการ (เพิ่มค่าด่วน)" },
              { k: "พื้นที่บริการ", v: "ทั่วประเทศ · ส่งทางไปรษณีย์" },
            ]} />
          </div>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <QuotableStatsStrip cluster="apostille" locale="th" variant="dark" />

      <AnswerBoxTop cluster="apostille" locale="th" />

      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8 sm:mt-10">
        <VoiceSearchFAQ topic="Apostille Thailand" faqs={voiceFaqs} entity="NYC Apostille Thailand" />
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        {APOSTILLE_REGIONS.map((region) => {
          const list = APOSTILLE_COUNTRIES.filter((c) => c.region === region.id);
          if (!list.length) return null;
          return (
            <section key={region.id} className="mb-12">
              <div className="mb-4 flex items-center gap-2">
                <Globe2 className="h-5 w-5 text-amber-600" />
                <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{region.label}</h2>
                <span className="text-sm text-slate-500">({list.length})</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((c) => (
                  <Link
                    key={c.slug}
                    to="/apostille/$country"
                    params={{ country: c.slug }}
                    className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                  >
                    <div className="text-3xl">{c.flag}</div>
                    <div className="flex-1">
                      <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                        {c.nameTh}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {c.isHague ? "Apostille" : "Legalization"} · เริ่ม ฿{c.priceFromTh.toLocaleString()}
                      </div>
                    </div>
                    <ShieldCheck className="h-4 w-4 text-emerald-500 opacity-0 transition group-hover:opacity-100" />
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      <RelatedHubGrid locale="th" />
      </div>

      <RelatedComparisons category="apostille" locale="th" />
      <TrustLayer variant="compact" />
          <HubCountryLinks service="apostille" lang="th" />
      <ServicePageEnhancer serviceName="Apostille" locale="th" />

      <PlanningToolsLinks locale="th" />
      </main>
  );
}
